import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import {
  Bytes,
  Firestore,
  GeoPoint,
  collection,
  collectionData,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { getStorage, ref, listAll, uploadBytes, deleteObject, getDownloadURL } from '@angular/fire/storage';
import { NetworkConstants, deepCopy, removeUndefined } from 'diwimamodelweb/lib';
import { diffObject, updatesToFirestoreUpdates } from 'diwimamodelweb/lib/utils/obejct-diff';
import { SettingsService } from 'src/app/shared/settings.service';
import { UserService } from 'src/app/shared/user.service';
import { TransactionLogService } from 'src/app/shared/transaction-log.service';
import { WorkOrder, WorkOrderImage, WorkOrderAttachment, MailDoc, isGeoFileName } from 'src/app/cockpit/work-orders/work-order.model';
import { applySharingFieldDeletes } from 'src/app/shared/context-rights-picker/sharing-fields';
import { TranslateService } from '@ngx-translate/core';

// CLEANUP
const WORK_ORDER_PRUNABLE_FIELDS: (keyof WorkOrder)[] = [
  'templateID', 'title', 'type', 'templateNote', 'note', 'link',
  'targetUserIds', 'responsibleUserId', 'assignedDogIds', 'parentWorkOrderId',
];

function isEmptyValue(v: any): boolean {
  return v === '' || (Array.isArray(v) && v.length === 0);
}

/** Create path: never write the field at all when it's just the empty default. */
function pruneEmptyFields(payload: Record<string, any>): Record<string, any> {
  const pruned = { ...payload };
  for (const key of WORK_ORDER_PRUNABLE_FIELDS) {
    if (isEmptyValue(pruned[key])) delete pruned[key];
  }
  return pruned;
}
function pruneEmptyUpdateFields(updateMap: Record<string, any>, newWorkOrder: WorkOrder): void {
  for (const key of WORK_ORDER_PRUNABLE_FIELDS) {
    if (isEmptyValue((newWorkOrder as any)[key])) {
      updateMap[key] = deleteField();
    }
  }
}

@Injectable({ providedIn: 'root' })
export class WorkOrderService {

  private Ifirestore = inject(Firestore);
  private storage = getStorage();
  private ngUnsubscribe = new Subject<void>();

  subscriptions: Map<string, Subscription> = new Map();

  workOrderSubject$ = new BehaviorSubject<WorkOrder[]>(undefined);
  workOrdersWithImages$ = new BehaviorSubject<Set<string>>(new Set());
  subbedContext: string | undefined = undefined;
  private checkedImageIds = new Set<string>();

  constructor(
    private settingsService: SettingsService,
    private userService: UserService,
    private transactionLogService: TransactionLogService,
    private translate: TranslateService
  ) {}

  // Subscription lifecycle

  observeData(contextId: string): void {
    const existing = this.subscriptions.get('1');
    const hasLiveSub = !!existing && !existing.closed;
    const ids = this.settingsService.selectedContextAndSubContextIds.value
    const superOrsubContext = this.settingsService.superContext.value || this.settingsService.subContext.value
    const subIds = this.settingsService.getSubcontextIds() // this.settingsService.selectedUserContext$.value.subContextIds
    
    const dataFromIds = superOrsubContext ? (ids ? ids?.join(',') : contextId) : contextId
    // console.log(' --- observeData workOrders ---', this.subbedContext, dataFromIds)

    const hasData = this.workOrderSubject$.value != null;

    if (this.subbedContext === dataFromIds && hasLiveSub && hasData) return;

    if (existing) existing.unsubscribe();

    if (this.subbedContext !== contextId && !!this.workOrderSubject$.value) {
      this.workOrderSubject$.next(undefined);
    }

    if (superOrsubContext) {
      const context = this.settingsService.selectedUserContext$.value
      const sub = this.observeWorkOrdersContextAndContextIds(contextId, subIds, context)
      this.subscriptions.set('1', sub)
    } else {
      const sub = this.observeWorkOrders(contextId);
      this.subscriptions.set('1', sub);
    }

    this.subbedContext = superOrsubContext ? dataFromIds : contextId
  }

  async removeSubs(): Promise<void> {
    this.ngUnsubscribe.next();
    this.subbedContext = undefined;
    this.subscriptions.forEach(s => s.unsubscribe());
    this.checkedImageIds.clear();
    this.workOrdersWithImages$.next(new Set());
    this.resetData();
    return Promise.resolve();
  }

  resetData(): void {
    this.workOrderSubject$.next(null);
  }

  private observeWorkOrders(contextId: string): Subscription {
    const q = query(
      collection(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER),
      where('contextId', '==', contextId),
    );

    return collectionData(q)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((docs: any[]) => {
        const orders = docs.map(d => this.normalizeTimestamps(d as WorkOrder));
        this.workOrderSubject$.next(orders);
        this.checkImagePresence(orders.map(o => o.identifier).filter((id): id is string => !!id));
      });
  }

  observeWorkOrdersContextAndContextIds(contextId: string, contextIds: string[], context) {
    // console.log('--- observeWorkOrdersContextAndContextIds ---', contextId, contextIds)
    const collRef = collection(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER);
    const CHUNK_SIZE = 30;
    const qy$ = [];

    qy$.push(collectionData(query(collRef, where('contextId', '==', contextId))));
    qy$.push(collectionData(query(collRef, where('contextIds', 'array-contains', contextId))));

    if (contextIds?.length) {//context.subContextIds
      for (let i = 0; i < contextIds.length; i += CHUNK_SIZE) {
        const chunk = contextIds.slice(i, i + CHUNK_SIZE);
        qy$.push(collectionData(query(collRef, where('contextId', 'in', chunk))));
      }
    }

    return combineLatest(...qy$)
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe({
      next: (results: any[][]) => {
        const byId = new Map<string, WorkOrder>();
        results.flat().forEach((d: any) => {
          const order = this.normalizeTimestamps(d as WorkOrder);
          if (order.identifier) byId.set(order.identifier, order);
        });
        const orders = [...byId.values()];
        this.workOrderSubject$.next(orders);
        this.checkImagePresence(orders.map(o => o.identifier).filter((id): id is string => !!id));
      },
      error: (err) => console.error('Firestore error:', err),
    });
  }

  private checkImagePresence(workOrderIds: string[]): void {
    const unchecked = workOrderIds.filter(id => !this.checkedImageIds.has(id));
    if (unchecked.length === 0) return;
    unchecked.forEach(id => this.checkedImageIds.add(id));

    Promise.all(
      unchecked.map(id =>
        getDocs(query(collection(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER, id, NetworkConstants.SUBCOLLECTION_IMAGE), limit(1)))
          .then(snap => ({ id, hasImages: !snap.empty }))
          .catch(() => ({ id, hasImages: false })),
      ),
    ).then(results => {
      const current = new Set(this.workOrdersWithImages$.value);
      let changed = false;
      results.forEach(r => {
        if (r.hasImages && !current.has(r.id)) { current.add(r.id); changed = true; }
      });
      if (changed) this.workOrdersWithImages$.next(current);
    });
  }

  //  Filtered observables

  /** All work orders including sub-orders */
  get allOrders$(): Observable<WorkOrder[]> {
    return this.workOrderSubject$.pipe(
      map(orders => orders ?? []),
    );
  }

  /** Top-level (non-sub) work orders */
  get parentOrders$(): Observable<WorkOrder[]> {
    return this.workOrderSubject$.pipe(
      map(orders => (orders ?? []).filter(o => !o.parentWorkOrderId)),
    );
  }

  /** Top-level work orders assigned to userId. */
  ownOrders$(userId: string): Observable<WorkOrder[]> {
    return this.workOrderSubject$.pipe(
      map(orders =>
        (orders ?? []).filter(o =>
          !o.parentWorkOrderId &&
          (o.targetUserIds ?? []).includes(userId),
        ),
      ),
    );
  }

  /** Sub-work-orders for a given parent */
  getSubOrders(parentWorkOrderId: string): Observable<WorkOrder[]> {
    const q = query(
      collection(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER),
      where('parentWorkOrderId', '==', parentWorkOrderId),
    );
    return (collectionData(q) as Observable<any[]>).pipe(
      map(docs => docs.map(d => this.normalizeTimestamps(d as WorkOrder))),
    );
  }

  // CRUD

  /** Resolves with the new document's id — needed to hang a sub-task or upload off a just-created draft. */
  async createWorkOrder(workOrder: Partial<WorkOrder>): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const contextId = this.settingsService.selectedUserContext$?.value?.identifier;
        if (!contextId) { reject('No context id'); return; }

        const user = await this.userService.user$.pipe(take(1)).toPromise();
        const docRef = doc(collection(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER));
        const now = new Date();

        const payload: any = pruneEmptyFields(removeUndefined({
          ...workOrder,
          identifier: docRef.id,
          contextId,
          createdUserId: workOrder.createdUserId || (user as any)?.identifier || (user as any)?.uid || '',
          createdUserDisplayName: workOrder.createdUserDisplayName
            || (user as any)?.displayName || (user as any)?.anzeigeName || '',
          createdDeviceName: workOrder.createdDeviceName || 'Web',
          creationDate: now,
          updateTimestamp: now,
          locationCoordinates: this.toGeoPoint(workOrder.locationCoordinates),
        }));

        const logItems = this.transactionLogService.createLog(
          user, docRef.id, NetworkConstants.COLLECTION_WORK_ORDER, payload,
        );

        await setDoc(docRef, payload);
        await this.transactionLogService.saveTransactionLog(logItems);
        resolve(docRef.id);
      } catch (error) {
        reject(error);
      }
    });
  }

  /** Deletes a work order together with any sub-orders under it — for cleaning up an abandoned draft. */
  async deleteWorkOrderWithSubOrders(workOrder: WorkOrder): Promise<void> {
    const subs = await this.getSubOrders(workOrder.identifier!).pipe(take(1)).toPromise();
    await Promise.all((subs ?? []).map(sub => this.deleteWorkOrder(sub)));
    await this.deleteWorkOrder(workOrder);
  }

  async updateWorkOrder(oldWorkOrder: WorkOrder, newWorkOrder: WorkOrder): Promise<void> {
    return new Promise(async (resolve, reject) => {

      const { steps: _oldSteps, locationCoordinates: _oldLoc, ...oldRest } = { ...oldWorkOrder } as any;
      const { steps: newSteps, locationCoordinates: _newLoc, ...newRest } = { ...newWorkOrder } as any;

      const updates = diffObject(oldRest, newRest, {});
      const updateMap = updatesToFirestoreUpdates(updates);

      // Always write steps directly when present.
      if (newSteps !== undefined) {
        updateMap['steps'] = newSteps;
      }

      updateMap['locationCoordinates'] = this.toGeoPoint(newWorkOrder.locationCoordinates);

      // Bypass for middle
      if (newWorkOrder.priority !== oldWorkOrder.priority) {
        updateMap['priority'] = newWorkOrder.priority;
      }

      for (const key of Object.keys(updateMap)) {
        const val = updateMap[key];
        if (
          val !== null && val !== undefined &&
          typeof val === 'object' &&
          !(val instanceof Date) &&
          !(val instanceof GeoPoint) &&
          !Array.isArray(val) &&
          val.constructor !== Object
        ) {
          const num = Number(val);
          updateMap[key] = isNaN(num) ? null : num;
        }
      }

      try {
        const user = await this.userService.user$.pipe(take(1)).toPromise();
        const list = await this.transactionLogService.determineDiff(
          user, updates, newWorkOrder.identifier, NetworkConstants.COLLECTION_WORK_ORDER,
        );
        updateMap['updateTimestamp'] = new Date();
        applySharingFieldDeletes(updateMap, oldWorkOrder as any, newWorkOrder as any);
        pruneEmptyUpdateFields(updateMap, newWorkOrder);

        await updateDoc(doc(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER, newWorkOrder.identifier), updateMap);
        await this.transactionLogService.saveTransactionLog(list);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  private workOrderStoragePath(workOrderId: string): string {
    return `workOrder/${workOrderId}`;
  }

  /** Deletes every object under a Storage folder, including nested prefixes. */
  private async deleteStorageFolder(path: string): Promise<void> {
    const res = await listAll(ref(this.storage, path));
    await Promise.all([
      ...res.items.map(item => deleteObject(item).catch(() => undefined)),
      ...res.prefixes.map(prefix => this.deleteStorageFolder(prefix.fullPath)),
    ]);
  }

  private async deleteWorkOrderFiles(workOrderId: string): Promise<void> {
    const imageDocs = await getDocs(
      collection(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER, workOrderId, NetworkConstants.SUBCOLLECTION_IMAGE),
    ).catch(() => null);

    await Promise.all([
      ...(imageDocs?.docs ?? []).map(d => deleteDoc(d.ref).catch(() => undefined)),
      this.deleteStorageFolder(this.workOrderStoragePath(workOrderId)).catch(() => undefined),
    ]);
  }

  async deleteWorkOrder(workOrder: WorkOrder): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const toDelete = JSON.parse(JSON.stringify(removeUndefined(deepCopy(workOrder, (workOrder as any).replacerFn))));
        const user = await this.userService.user$.pipe(take(1)).toPromise();

        const logItem = this.transactionLogService.deleteDocumentLog(
          user, workOrder.identifier, NetworkConstants.COLLECTION_WORK_ORDER, toDelete,
        );

        await this.transactionLogService.saveTransactionLog([logItem]);

        await this.deleteWorkOrderFiles(workOrder.identifier).catch(err =>
          console.error('WorkOrder file cleanup failed', workOrder.identifier, err),
        );

        await deleteDoc(doc(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER, workOrder.identifier));

        this.checkedImageIds.delete(workOrder.identifier);
        this.markHasImages(workOrder.identifier, false);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  async deleteWorkOrders(workOrders: WorkOrder[]): Promise<void> {
    await Promise.all(workOrders.map(wo => this.deleteWorkOrder(wo)));
  }

  private static readonly IMAGE_THUMB_MAX = 200;

  /** workOrder/<wo-id>/images */
  private imagesPath(workOrderId: string): string {
    return `workOrder/${workOrderId}/images`;
  }

  observeImages(workOrderId: string): Observable<WorkOrderImage[]> {
    const imagesRef = collection(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER, workOrderId, NetworkConstants.SUBCOLLECTION_IMAGE);
    // idField exposes the document id (the uuid) — needed to delete the doc and to
    // derive the Storage object names.
    return (collectionData(imagesRef, { idField: 'docId' }) as Observable<WorkOrderImage[]>)
      .pipe(map(images => this.dedupeImages(images)));
  }

  private dedupeImages(images: WorkOrderImage[]): WorkOrderImage[] {
    const best = new Map<string, WorkOrderImage>();

    for (const image of images ?? []) {
      const key = this.canonicalImageKey(image);
      if (!key) continue;
      const current = best.get(key);
      if (!current || this.imageRank(image) < this.imageRank(current)) best.set(key, image);
    }
    return [...best.values()];
  }

  private canonicalImageKey(image: WorkOrderImage): string {
    const raw = image?.identifier ?? image?.docId ?? '';
    return raw
      .replace(/^(?:preview_)+/i, '')
      .replace(/\.[a-z0-9]+$/i, '')
      || (image?.docId ?? '');
  }

  private imageRank(image: WorkOrderImage): number {
    const prefixes = ((image?.identifier ?? '').match(/preview_/gi) ?? []).length;
    return (image?.thumbnailData ? 0 : 10) + prefixes;
  }

  async uploadImage(workOrderId: string, file: File): Promise<void> {
    const contextId = this.settingsService.selectedUserContext$?.value?.identifier ?? null;
    const id = this.newImageId();
    const base = this.imagesPath(workOrderId);

    // The full image keeps the original bytes and type — no re-encode, no extension.
    const fullBytes = new Uint8Array(await file.arrayBuffer());
    const fullType = file.type || 'application/octet-stream';

    const bitmap = await this.decodeImage(file);
    let thumb: Uint8Array;
    try {
      thumb = await this.encodeFrom(bitmap, WorkOrderService.IMAGE_THUMB_MAX, 0.7, 'image/jpeg');
    } finally {
      bitmap.close?.();
    }

    await uploadBytes(ref(this.storage, `${base}/${id}`), fullBytes, { contentType: fullType });

    const now = new Date();
    await setDoc(doc(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER, workOrderId, NetworkConstants.SUBCOLLECTION_IMAGE, id), {
      identifier: `preview_${id}`,
      parentId: workOrderId,
      contextId,
      thumbnailData: Bytes.fromUint8Array(thumb),
      creationDate: now,
      timeStamp: now,
      updateTimestamp: now,
    });

    this.markHasImages(workOrderId, true);
  }

  private storageCandidates(id: string): string[] {
    return [
      id,
      `${id}.jpg`,
      `preview_${id}`,
      `preview_${id}.jpg`,
      `preview_preview_${id}`,
      `preview_preview_${id}.jpg`,
    ];
  }

  /** Download URL of the full-size image, falling back to the preview object. */
  async imageDownloadUrl(workOrderId: string, image: WorkOrderImage): Promise<string | null> {
    const id = this.imageId(image);
    if (!id) return null;
    const base = this.imagesPath(workOrderId);
    for (const name of this.storageCandidates(id)) {
      try {
        return await getDownloadURL(ref(this.storage, `${base}/${name}`));
      } catch { /* fall through to the next candidate */ }
    }
    return null;
  }

  /**
   * Keep the grid's media-column flag in sync after an upload/delete.
   * checkImagePresence() only ever probes a work order once, so without this the
   * icon wouldn't appear until a full reload.
   */
  private markHasImages(workOrderId: string, hasImages: boolean): void {
    const current = new Set(this.workOrdersWithImages$.value);
    if (current.has(workOrderId) === hasImages) return;
    if (hasImages) current.add(workOrderId);
    else current.delete(workOrderId);
    this.workOrdersWithImages$.next(current);
  }

  /** Removes the Firestore doc and both Storage objects. */
  async deleteImage(workOrderId: string, image: WorkOrderImage): Promise<void> {
    const id = this.imageId(image);
    if (id) {
      const base = this.imagesPath(workOrderId);
      // Sweep both naming conventions and tolerate already-missing files, so legacy
      // .jpg images and half-cleaned ones are still removed completely.
      await Promise.all(
        this.storageCandidates(id).map(name =>
          deleteObject(ref(this.storage, `${base}/${name}`)).catch(() => undefined),
        ),
      );
    }
 
    const key = this.canonicalImageKey(image);
    const all = await getDocs(
      collection(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER, workOrderId, NetworkConstants.SUBCOLLECTION_IMAGE),
    );
    const toDelete = all.docs.filter(d =>
      this.canonicalImageKey({ ...(d.data() as WorkOrderImage), docId: d.id }) === key,
    );
    if (toDelete.length) {
      await Promise.all(toDelete.map(d => deleteDoc(d.ref).catch(() => undefined)));
    } else {
      const docId = image.docId ?? id;
      if (docId) {
        await deleteDoc(doc(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER, workOrderId, NetworkConstants.SUBCOLLECTION_IMAGE, docId));
      }
    }

    // Drop the grid's media flag once the last image is gone.
    const remaining = await getDocs(
      query(collection(this.Ifirestore, NetworkConstants.COLLECTION_WORK_ORDER, workOrderId, NetworkConstants.SUBCOLLECTION_IMAGE), limit(1)),
    );
    this.markHasImages(workOrderId, !remaining.empty);
  }

  /**
   * The uuid: the doc id, or recovered from `identifier` — `preview_<uuid>` now, or
   * the legacy `preview_<uuid>.jpg` written by older iOS builds.
   */
  private imageId(image: WorkOrderImage): string | null {
    if (image.docId) return image.docId;
    const match = (image.identifier ?? '').match(/^preview_(.+?)(?:\.[a-z0-9]+)?$/i);
    return match ? match[1] : null;
  }

  private newImageId(): string {
    return (globalThis.crypto as any)?.randomUUID?.()
      ?? `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }

  /** Decoded once and reused for every derived size. */
  private async decodeImage(file: File): Promise<ImageBitmap> {
    try {
      return await createImageBitmap(file);
    } catch {
      // e.g. HEIC in a browser that can't decode it — the original still uploads fine,
      // but we can't derive a preview/thumbnail from it.
      const img_format_cannot_processed = this.translate.instant('img_format_cannot_processed')
      throw new Error(img_format_cannot_processed);
    }
  }

  /** Draw the bitmap into a canvas capped at `maxSize` on its longest edge. */
  private async encodeFrom(
    bitmap: ImageBitmap,
    maxSize: number,
    quality: number,
    type: string,
  ): Promise<Uint8Array> {
    const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const img_not_processed = this.translate.instant('img_not_processed')
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error(img_not_processed);
    ctx.drawImage(bitmap, 0, 0, width, height);

    const img_not_codified = this.translate.instant('img_not_codified')
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        b => (b ? resolve(b) : reject(new Error(img_not_codified))),
        type,
        quality,
      ),
    );
    return new Uint8Array(await blob.arrayBuffer());
  }

  // ── Attachments (Storage only — no Firestore mirror, same as game records) ──

  /** workOrder/<wo-id>/attachments */
  private attachmentsPath(workOrderId: string): string {
    return `workOrder/${workOrderId}/attachments`;
  }

  /** workOrder/<wo-id>/attachments/steps/<step-id> */
  private stepAttachmentsPath(workOrderId: string, stepId: string): string {
    return `${this.attachmentsPath(workOrderId)}/steps/${stepId}`;
  }


  private sanitizeFileName(name: string): string {
    const clean = (name ?? '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9._-]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^[-.]+/, '')
      .slice(0, 120);
    return clean || 'datei';
  }

  private stepFileName(file: File, ownerId?: string | null): string {
    const safe = this.sanitizeFileName(file.name);
    return ownerId ? `${ownerId}_${safe}` : safe;
  }

  private async listFolder(path: string, ownerId?: string): Promise<WorkOrderAttachment[]> {
    // listAll returns only direct items — the nested steps/ folder surfaces as a
    // prefix, so work-order attachments never pick up step files.
    const res = await listAll(ref(this.storage, path));
    return Promise.all(
      res.items.map(async item => ({
        name: item.name,
        reference: item.fullPath,
        url: await getDownloadURL(ref(this.storage, item.fullPath)),
        ...(ownerId ? { ownerId } : {}),
      })),
    );
  }

  listAttachments(workOrderId: string): Promise<WorkOrderAttachment[]> {
    return this.listFolder(this.attachmentsPath(workOrderId));
  }


  async listStepAttachments(
    workOrderId: string,
    stepId: string,
    ownerIds: string[] = [],
  ): Promise<WorkOrderAttachment[]> {
    const base = this.stepAttachmentsPath(workOrderId, stepId);
    const res = await listAll(ref(this.storage, base));

    const knownOwners = [...ownerIds].filter(Boolean).sort((a, b) => b.length - a.length);

    const direct = await Promise.all(
      res.items.map(async item => {
        const ownerId = knownOwners.find(id => item.name.startsWith(`${id}_`));
        return {
          name: ownerId ? item.name.slice(ownerId.length + 1) : item.name,
          reference: item.fullPath,
          url: await getDownloadURL(ref(this.storage, item.fullPath)),
          ...(ownerId ? { ownerId } : {}),
        };
      }),
    );

    // TODO: delete this later
    const legacyScoped = await Promise.all(
      res.prefixes.map(prefix => this.listFolder(prefix.fullPath, prefix.name)),
    );

    return [...direct, ...legacyScoped.flat()];
  }

  async listGeoAttachments(workOrderId: string, ownerIds: string[] = []): Promise<WorkOrderAttachment[]> {
    const base = this.workOrderStoragePath(workOrderId);
    const imagesPath = `${base}/images`;
    const knownOwners = [...new Set(ownerIds)].filter(Boolean).sort((a, b) => b.length - a.length);

    // Step files: steps/<step-id>/<ownerId>_<file>, legacy steps/<step-id>/<ownerId>/<file>
    const withOwner = (fullPath: string, name: string): { name: string; ownerId?: string } => {
      const rel = fullPath.slice(base.length + 1).split('/');
      if (rel[0] !== 'attachments' || rel[1] !== 'steps') return { name };
      if (rel.length === 5) return { name, ownerId: rel[3] };
      const ownerId = knownOwners.find(id => name.startsWith(`${id}_`));
      return ownerId ? { name: name.slice(ownerId.length + 1), ownerId } : { name };
    };

    const collect = async (path: string): Promise<WorkOrderAttachment[]> => {
      const res = await listAll(ref(this.storage, path));
      const own = await Promise.all(
        res.items
          .filter(item => isGeoFileName(item.name))
          .map(async item => ({
            ...withOwner(item.fullPath, item.name),
            reference: item.fullPath,
            url: await getDownloadURL(ref(this.storage, item.fullPath)),
          })),
      );
      const nested = await Promise.all(
        res.prefixes
          .filter(prefix => prefix.fullPath !== imagesPath)
          .map(prefix => collect(prefix.fullPath)),
      );
      return [...own, ...nested.flat()];
    };

    return collect(base);
  }

  async uploadAttachment(workOrderId: string, file: File): Promise<WorkOrderAttachment> {
    const name = this.sanitizeFileName(file.name);
    const path = `${this.attachmentsPath(workOrderId)}/${name}`;
    await uploadBytes(ref(this.storage, path), file);
    return { name, reference: path, url: await getDownloadURL(ref(this.storage, path)) };
  }

  async uploadStepAttachment(
    workOrderId: string,
    stepId: string,
    file: File,
    ownerId?: string | null,
  ): Promise<WorkOrderAttachment> {
    const path = `${this.stepAttachmentsPath(workOrderId, stepId)}/${this.stepFileName(file, ownerId)}`;
    await uploadBytes(ref(this.storage, path), file);
    return {
      // Display name stays the plain file name — the owner prefix is storage detail.
      name: this.sanitizeFileName(file.name),
      reference: path,
      url: await getDownloadURL(ref(this.storage, path)),
      ...(ownerId ? { ownerId } : {}),
    };
  }

  async uploadStepAttachments(
    workOrderId: string,
    stepId: string,
    files: File[],
    ownerId?: string | null,
  ): Promise<WorkOrderAttachment[]> {
    const uploaded: WorkOrderAttachment[] = [];
    for (const file of files) {
      uploaded.push(await this.uploadStepAttachment(workOrderId, stepId, file, ownerId));
    }
    return uploaded;
  }

  deleteAttachment(reference: string): Promise<void> {
    return deleteObject(ref(this.storage, reference));
  }

  // Mail

  async createMailDocs(mails: MailDoc[]): Promise<void> {
    await Promise.all(mails.map(mail => {
      const docRef = doc(collection(this.Ifirestore, 'Mail'));
      return setDoc(docRef, removeUndefined(mail));
    }));
  }

  private normalizeTimestamps(order: WorkOrder): WorkOrder {
    return {
      ...order,
      dueDate:               this.toDate(order.dueDate),
      plannedExecutionDate:  this.toDate(order.plannedExecutionDate),
      creationDate:          this.toDate(order.creationDate),
      updateTimestamp:       this.toDate(order.updateTimestamp),
    };
  }

  private toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value?.seconds) return new Date(value.seconds * 1000);
    return null;
  }

  private toGeoPoint(coords: any): GeoPoint | null {
    if (!coords) return null;
    if (coords instanceof GeoPoint) return coords;
    if (coords?.latitude != null && coords?.longitude != null) {
      return new GeoPoint(coords.latitude, coords.longitude);
    }
    return null;
  }
}
